import React, { useState } from 'react';
import {
  Container,
  GridLegacy as Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tab,
  Tabs,
  FormGroup,
  FormControlLabel,
  Checkbox,
  SelectChangeEvent,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  EmojiEvents as TrophyIcon,
  Speed as SpeedIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Bar, Line } from 'react-chartjs-2';
import { format } from 'date-fns';
import api from '../services/api';
import '../utils/chartSetup'; // Initialize Chart.js

// Types
interface TeamConfig {
  name: string;
  color: string;
}

interface TeamMetrics {
  totalSales: number;
  totalCalls: number;
  totalContacts: number;
  contactRate: string;
  avgTalkPercent: string;
  avgSPH: string;
  conversionRate: string;
  callToSaleRatio: string;
  avgUtilization: string;
  avgWinRate?: string;
}

interface DayMetric {
  dayName: string;
  totalSales: number;
  totalCalls?: number;
}

interface TopPerformer {
  name: string;
  totalSales: number;
  totalCalls: number;
}

interface TeamData {
  metrics: TeamMetrics;
  recordCount: number;
  byDay?: DayMetric[];
  topPerformers?: TopPerformer[];
}

interface Anomaly {
  severity: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  type: string;
}

interface ComparisonData {
  teams: Record<string, TeamData>;
  anomalies: Anomaly[];
  dateRange: {
    start?: string;
    end?: string;
  };
}

type DateFilter = 'currentWeek' | 'previousDay' | 'previousWeek' | 'dateRange';
type SeverityColor = 'error' | 'warning' | 'info';

const AVAILABLE_TEAMS: TeamConfig[] = [
  { name: 'SoniqCX', color: '#1976d2' },
  { name: 'Response BPO', color: '#dc004e' },
  { name: 'Waistline Warriors', color: '#2e7d32' },
  { name: 'Dose Dealers', color: '#ed6c02' },
  { name: 'Trim Team', color: '#9c27b0' }
];

const TeamComparison: React.FC = () => {
  const [dateFilter, setDateFilter] = useState<DateFilter>('currentWeek');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedTeams, setSelectedTeams] = useState<string[]>(['SoniqCX', 'Response BPO']);

  const {
    data: comparisonData,
    isLoading,
    error
  } = useQuery<ComparisonData>({
    queryKey: ['teamComparison', dateFilter, startDate, endDate, selectedTeams],
    queryFn: () => api.getTeamComparison(dateFilter, selectedTeams, startDate || undefined, endDate || undefined),
    refetchInterval: 5 * 60 * 1000,
    enabled: Boolean((dateFilter !== 'dateRange' || (Boolean(startDate) && Boolean(endDate))) && selectedTeams.length >= 2)
  });

  const teams = comparisonData?.teams || {};
  const anomalies = comparisonData?.anomalies || [];
  const dateRange = comparisonData?.dateRange || {};

  const handleTeamToggle = (teamName: string) => {
    setSelectedTeams(prev => {
      if (prev.includes(teamName)) {
        if (prev.length <= 2) {
          alert('You must select at least 2 teams to compare');
          return prev;
        }
        return prev.filter(t => t !== teamName);
      } else {
        return [...prev, teamName];
      }
    });
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const getTeamColor = (teamName: string): string => {
    const team = AVAILABLE_TEAMS.find(t => t.name === teamName);
    return team?.color || '#666666';
  };

  const getFilterLabel = (): string => {
    switch (dateFilter) {
      case 'currentWeek':
        return 'Current Week (Sun-Sat)';
      case 'previousWeek':
        return 'Previous Week (Sun-Sat)';
      case 'dateRange':
        if (dateRange.start && dateRange.end) {
          return `${format(new Date(dateRange.start), 'MMM dd')} - ${format(new Date(dateRange.end), 'MMM dd, yyyy')}`;
        }
        return 'Date Range';
      default:
        return '';
    }
  };

  const getSeverityColor = (severity: Anomaly['severity']): SeverityColor => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'info';
    }
  };

  const getSeverityIcon = (severity: Anomaly['severity']): React.ReactElement => {
    switch (severity) {
      case 'high': return <ErrorIcon />;
      case 'medium': return <WarningIcon />;
      case 'low': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  interface MetricCardProps {
    title: string;
    icon?: React.ReactElement;
  }

  const MetricCard: React.FC<MetricCardProps> = ({ title, icon }) => {
    const getBest = (metric: keyof TeamMetrics): string | null => {
      let best: string | null = null;
      let bestValue = -Infinity;
      
      selectedTeams.forEach(teamName => {
        const value = parseFloat(String(teams[teamName]?.metrics?.[metric] || 0));
        if (value > bestValue) {
          bestValue = value;
          best = teamName;
        }
      });
      
      return best;
    };

    const metricKey = title.toLowerCase().replace(/ |%/g, '');
    const metricMap: Record<string, keyof TeamMetrics> = {
      'totalsales': 'totalSales',
      'totalcalls': 'totalCalls',
      'totalcontacts': 'totalContacts',
      'contactrate': 'contactRate',
      'talk': 'avgTalkPercent',
      'salesperhour': 'avgSPH',
      'conversionrate': 'conversionRate',
      'callspersale': 'callToSaleRatio',
      'utilization': 'avgUtilization'
    };
    
    const actualMetric = metricMap[metricKey];
    const bestTeam = getBest(actualMetric);

    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            {icon && <Box sx={{ mr: 1, color: 'primary.main' }}>{icon}</Box>}
            <Typography variant="subtitle2" color="textSecondary">
              {title}
            </Typography>
          </Box>
          
          <Grid container spacing={1}>
            {selectedTeams.map(teamName => {
              const value = teams[teamName]?.metrics?.[actualMetric] || 0;
              const isBest = teamName === bestTeam;
              
              return (
                <Grid item xs={12 / Math.min(selectedTeams.length, 2)} key={teamName}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {teamName}
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      color: isBest ? 'success.main' : 'text.primary',
                      fontWeight: isBest ? 'bold' : 'normal'
                    }}>
                      {formatNumber(Number(value))}{actualMetric?.includes('Rate') || actualMetric?.includes('Utilization') || actualMetric?.includes('Percent') ? '%' : ''}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {(error as any).response?.data?.error || 'Failed to load team comparison data.'}
        </Alert>
      </Container>
    );
  }

  if (selectedTeams.length < 2) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">
          Please select at least 2 teams to compare.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Team Performance Comparison
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Multi-Team Analytics & Anomaly Detection
          </Typography>
        </Box>
        
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={dateFilter}
              onChange={(e: SelectChangeEvent<DateFilter>) => setDateFilter(e.target.value as DateFilter)}
            >
              <MenuItem value="currentWeek">Current Week</MenuItem>
              <MenuItem value="previousDay">Previous Day</MenuItem>
              <MenuItem value="previousWeek">Previous Week</MenuItem>
              <MenuItem value="dateRange">Date Range</MenuItem>
            </Select>
          </FormControl>
          
          {dateFilter === 'dateRange' && (
            <>
              <TextField
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                label="End Date"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
            </>
          )}
        </Box>
      </Box>

      {/* Team Selection Checkboxes */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Select Teams to Compare (minimum 2):
        </Typography>
        <FormGroup row>
          {AVAILABLE_TEAMS.map(team => (
            <FormControlLabel
              key={team.name}
              control={
                <Checkbox
                  checked={selectedTeams.includes(team.name)}
                  onChange={() => handleTeamToggle(team.name)}
                  sx={{ color: team.color, '&.Mui-checked': { color: team.color } }}
                />
              }
              label={team.name}
            />
          ))}
        </FormGroup>
      </Paper>

      {/* Date Range Display */}
      <Box mb={3}>
        <Chip 
          label={getFilterLabel()} 
          color="primary" 
          variant="outlined"
          icon={<CompareIcon />}
        />
        {selectedTeams.map(teamName => (
          <Chip
            key={teamName}
            label={`${teams[teamName]?.recordCount || 0} ${teamName} records`}
            sx={{ ml: 1, borderColor: getTeamColor(teamName), color: getTeamColor(teamName) }}
            variant="outlined"
          />
        ))}
      </Box>

      {/* Anomalies Alert */}
      {anomalies.length > 0 && (
        <Box mb={3}>
          <Alert severity="warning" icon={<WarningIcon />}>
            <Typography variant="subtitle2" gutterBottom>
              {anomalies.length} Anomal{anomalies.length === 1 ? 'y' : 'ies'} Detected
            </Typography>
            <Typography variant="body2">
              Data quality issues and performance anomalies have been identified.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Overview" />
          <Tab label="Day-of-Week Analysis" />
          <Tab label="Top Performers" />
          <Tab label={`Anomalies ${anomalies.length > 0 ? `(${anomalies.length})` : ''}`} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
          {/* Key Metrics Comparison */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Key Performance Indicators
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Total Sales" icon={<TrophyIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Total Calls" icon={<PhoneIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Total Contacts" icon={<PeopleIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Contact Rate" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Conversion Rate" icon={<TrendingUpIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Talk %" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Sales Per Hour" icon={<SpeedIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Utilization" />
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Sales & Calls Comparison
                </Typography>
                <Box sx={{ height: 300 }}>
                  <Bar 
                    data={{
                      labels: ['Sales', 'Calls'],
                      datasets: selectedTeams.map(teamName => ({
                        label: teamName,
                        data: [
                          teams[teamName]?.metrics?.totalSales || 0,
                          teams[teamName]?.metrics?.totalCalls || 0
                        ],
                        backgroundColor: getTeamColor(teamName),
                      }))
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                        },
                      },
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top' as const,
                        },
                      },
                    }}
                  />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Efficiency Metrics (%)
                </Typography>
                <Box sx={{ height: 300 }}>
                  <Bar 
                    data={{
                      labels: ['Contact %', 'Conversion %', 'Win Rate %'],
                      datasets: selectedTeams.map(teamName => ({
                        label: teamName,
                        data: [
                          parseFloat(teams[teamName]?.metrics?.contactRate || '0'),
                          parseFloat(teams[teamName]?.metrics?.conversionRate || '0'),
                          parseFloat(teams[teamName]?.metrics?.avgWinRate || '0')
                        ],
                        backgroundColor: getTeamColor(teamName),
                      }))
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                        },
                      },
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top' as const,
                        },
                      },
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {activeTab === 1 && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Performance by Day of Week
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Daily Sales Comparison
                </Typography>
                <Box sx={{ height: 300 }}>
                  <Line
                    data={{
                      labels: teams[selectedTeams[0]]?.byDay?.map(day => day.dayName) || [],
                      datasets: selectedTeams.map(teamName => ({
                        label: teamName,
                        data: teams[teamName]?.byDay?.map(day => day.totalSales) || [],
                        borderColor: getTeamColor(teamName),
                        backgroundColor: getTeamColor(teamName),
                        tension: 0.3,
                      }))
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                        },
                      },
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top' as const,
                        },
                      },
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {activeTab === 2 && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Top 10 Performers by Team
          </Typography>
          
          <Grid container spacing={3}>
            {selectedTeams.map(teamName => (
              <Grid item xs={12} md={6} key={teamName}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: getTeamColor(teamName) }}>
                    {teamName} Top Performers
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Rank</TableCell>
                          <TableCell>Agent</TableCell>
                          <TableCell align="right">Sales</TableCell>
                          <TableCell align="right">Calls</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {teams[teamName]?.topPerformers?.map((agent, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{agent.name}</TableCell>
                            <TableCell align="right"><strong>{formatNumber(agent.totalSales)}</strong></TableCell>
                            <TableCell align="right">{formatNumber(agent.totalCalls)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {activeTab === 3 && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Detected Anomalies & Data Quality Issues
          </Typography>
          
          {anomalies.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <TrophyIcon sx={{ fontSize: 60, color: 'success.main' }} />
                <Typography variant="h6" color="success.main">
                  No Anomalies Detected
                </Typography>
                <Typography color="textSecondary">
                  All teams appear to have consistent and reliable data.
                </Typography>
              </Box>
            </Paper>
          ) : (
            <Box>
              {anomalies.map((anomaly, idx) => (
                <Alert 
                  key={idx} 
                  severity={getSeverityColor(anomaly.severity)}
                  icon={getSeverityIcon(anomaly.severity)}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>{anomaly.title}</strong>
                  </Typography>
                  <Typography variant="body2">
                    {anomaly.description}
                  </Typography>
                  <Box mt={1}>
                    <Chip 
                      size="small" 
                      label={anomaly.type.replace('_', ' ').toUpperCase()} 
                      color={getSeverityColor(anomaly.severity)}
                      variant="outlined"
                    />
                  </Box>
                </Alert>
              ))}
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default TeamComparison;
